import { Breadcrumb } from 'antd';
import { useLayoutEffect, useRef } from 'react';
import { Link, NavigationType, useLocation, useMatches, useNavigationType } from 'react-router-dom';

import {
  appNavigationState,
  appRouteHandle,
  createRestoreNavigationState,
  resolveReturnTarget,
  scrollRestorationKey,
} from './route-navigation.js';

export function AppBreadcrumb() {
  const location = useLocation();
  const matches = useMatches();
  const currentHandle = [...matches]
    .reverse()
    .map((match) => appRouteHandle(match.handle))
    .find((handle) => handle?.breadcrumb !== undefined);
  if (!currentHandle?.breadcrumb) return null;

  const returnTarget = currentHandle.listPath
    ? resolveReturnTarget(location.state, currentHandle.listPath)
    : undefined;
  return (
    <Breadcrumb
      aria-label="页面路径"
      className="app-breadcrumb"
      items={[
        ...(returnTarget
          ? [
              {
                title: (
                  <Link
                    state={{
                      ...returnTarget.parentState,
                      ...createRestoreNavigationState(returnTarget.scrollKey),
                    }}
                    to={returnTarget.path}
                  >
                    {returnTarget.label}
                  </Link>
                ),
              },
            ]
          : []),
        { title: currentHandle.breadcrumb },
      ]}
    />
  );
}

export function AppScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef<typeof location | undefined>(undefined);
  const key = scrollRestorationKey(location);
  const historyKey = `entry:${location.key}`;
  const mainScrollTop = appNavigationState(location.state)?.mainScrollTop;

  useLayoutEffect(() => {
    const container = document.querySelector<HTMLElement>('.app-content');
    if (!container) return;
    const scrollContainer = container;

    const prior = previous.current;
    const state = appNavigationState(location.state);
    const priorState = appNavigationState(prior?.state);
    const samePageQuery =
      prior?.pathname === location.pathname &&
      prior.search !== location.search &&
      navigationType !== NavigationType.Pop;
    const explicitRestore =
      (state?.restoreScrollKey !== undefined &&
        state.restoreScrollKey !== priorState?.restoreScrollKey) ||
      (mainScrollTop !== undefined && mainScrollTop !== priorState?.mainScrollTop);
    // Return links can carry a shared restore key; POP still needs each entry's own position.
    const stored =
      navigationType === NavigationType.Pop
        ? (readScrollPosition(historyKey) ?? readScrollPosition(key))
        : readScrollPosition(key);
    const target =
      samePageQuery && !explicitRestore
        ? (readScrollPosition(`entry:${prior.key}`) ??
          readScrollPosition(scrollRestorationKey(prior)) ??
          scrollContainer.scrollTop)
        : (stored ?? mainScrollTop ?? 0);
    previous.current = location;
    writeScrollPosition(historyKey, target);
    if (samePageQuery || (stored === undefined && mainScrollTop !== undefined))
      writeScrollPosition(key, target);
    let restoring = true;
    let restoreFrame = 0;
    let restoreTimer = 0;
    const observer = new MutationObserver(restore);
    function stopRestoring() {
      restoring = false;
      observer.disconnect();
      window.clearTimeout(restoreTimer);
      window.cancelAnimationFrame(restoreFrame);
    }
    function restore() {
      const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      scrollContainer.scrollTop = Math.min(target, maxScrollTop);
      if (maxScrollTop >= target) stopRestoring();
    }
    function save() {
      if (!restoring) {
        writeScrollPosition(key, scrollContainer.scrollTop, historyKey);
      }
    }

    scrollContainer.addEventListener('scroll', save, { passive: true });
    observer.observe(scrollContainer, { childList: true, subtree: true });
    restoreFrame = window.requestAnimationFrame(restore);
    restoreTimer = window.setTimeout(stopRestoring, 2_000);

    return () => {
      stopRestoring();
      scrollContainer.removeEventListener('scroll', save);
    };
  }, [key, historyKey, mainScrollTop, location, navigationType]);

  return null;
}

const SCROLL_STORAGE_KEY = 'badminton-scroll-positions';

function readScrollPosition(key: string): number | undefined {
  try {
    const positions = JSON.parse(
      window.sessionStorage.getItem(SCROLL_STORAGE_KEY) ?? '{}',
    ) as Record<string, unknown>;
    const position = positions[key];
    return typeof position === 'number' && Number.isFinite(position) && position >= 0
      ? position
      : undefined;
  } catch {
    return undefined;
  }
}

function writeScrollPosition(key: string, position: number, historyKey?: string): void {
  try {
    const positions = JSON.parse(
      window.sessionStorage.getItem(SCROLL_STORAGE_KEY) ?? '{}',
    ) as Record<string, unknown>;
    positions[key] = position;
    if (historyKey) positions[historyKey] = position;
    window.sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Session storage can be unavailable in hardened browser contexts.
  }
}
